import { Component } from '@theme/component';

/**
 * Horizontal format carousel with previous/next controls.
 *
 * Wired declaratively from the section markup: `ref="scroller"` on the scrolling
 * track, `on:click="/previous"` and `on:click="/next"` on the arrow buttons.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [previousButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<Refs>}
 */
class OkiShopFormat extends Component {
  requiredRefs = ['scroller'];

  connectedCallback() {
    super.connectedCallback();

    this.#updateControls();
    this.refs.scroller.addEventListener('scroll', this.#updateControls, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.scroller?.removeEventListener('scroll', this.#updateControls);
  }

  previous() {
    this.#scrollByCard(-1);
  }

  next() {
    this.#scrollByCard(1);
  }

  /**
   * Scrolls the track by exactly one card, gap included.
   *
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByCard(direction) {
    const { scroller } = this.refs;
    const card = scroller.firstElementChild;
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const step = card instanceof HTMLElement ? card.offsetWidth + gap : scroller.clientWidth * 0.8;

    scroller.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  /**
   * Disables an arrow once its end of the track is reached.
   */
  #updateControls = () => {
    const { scroller, previousButton, nextButton } = this.refs;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    // Sub-pixel layout rounding means scrollLeft never lands exactly on the bounds.
    if (previousButton) previousButton.disabled = scroller.scrollLeft <= 1;
    if (nextButton) nextButton.disabled = scroller.scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get('oki-shop-format')) {
  customElements.define('oki-shop-format', OkiShopFormat);
}
